import type { DirectiveBinding } from "vue"

// 🔧 扩展 HTMLElement 类型，添加自定义属性
declare module 'vue' {
  interface HTMLElement {
    _longpressHandlers?: {
      start: (e: TouchEvent | MouseEvent) => void
      cancel: () => void
      handleClick: (e: Event) => void
    }
    _longpressBinding?: DirectiveBinding
  }
}

const longpress = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    // 🔧 存储 binding 到元素上，以便 start 函数能获取最新状态
    el._longpressBinding = binding
    
    // 🔧 支持两种格式：直接传函数 或 传对象 { handler, disabled }
    const isFunction = typeof binding.value === "function"
    const isObject = typeof binding.value === "object" && binding.value !== null
    
    if (!isFunction && !isObject) {
      console.warn("v-longpress 需要绑定一个函数或对象 { handler, disabled }")
      return
    }

    const handler = isFunction ? binding.value : binding.value.handler

    if (typeof handler !== "function") {
      console.warn("v-longpress handler 必须是一个函数")
      return
    }

    let timer: any = null
    let startEvent: TouchEvent | MouseEvent | null = null
    let hasTriggered = false
    const duration = 600 // 长按时长，可自行调整

    const getPoint = (e: TouchEvent | MouseEvent) => {
      if ((e as TouchEvent).touches && (e as TouchEvent).touches[0]) {
        const t = (e as TouchEvent).touches[0]
        return { clientX: t.clientX, clientY: t.clientY, pageX: t.pageX, pageY: t.pageY }
      }
      const m = e as MouseEvent
      return { clientX: m.clientX, clientY: m.clientY, pageX: m.pageX, pageY: m.pageY }
    }

    const start = (e: TouchEvent | MouseEvent) => {
      // 🔧 从元素上读取最新的 binding，获取当前的 disabled 状态
      const currentBinding = el._longpressBinding
      if (currentBinding) {
        const currentValue = currentBinding.value
        const isCurrentObject = typeof currentValue === "object" && currentValue !== null
        const isCurrentDisabled = isCurrentObject && currentValue.disabled
        
        // 如果当前是禁用状态，不执行长按
        if (isCurrentDisabled) {
          return
        }
      }
      
      startEvent = e
      hasTriggered = false
      if (timer === null) {
        timer = setTimeout(() => {
          // 只有在长按触发时才阻止默认行为
          if (startEvent && !hasTriggered) {
            hasTriggered = true
            startEvent.preventDefault()
            const target = startEvent.target as HTMLElement
            const rect = target?.getBoundingClientRect?.()
            const point = getPoint(startEvent)
            
            // 🔧 获取最新的 handler
            const latestBinding = el._longpressBinding
            if (latestBinding) {
              const latestValue = latestBinding.value
              const latestHandler = typeof latestValue === "function" 
                ? latestValue 
                : latestValue.handler
              
              if (typeof latestHandler === "function") {
                latestHandler({
                  event: startEvent,
                  point,
                  targetRect: rect,
                })
              }
            }
          }
        }, duration)
      }
    }

    const cancel = () => {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
        startEvent = null
        hasTriggered = false
      }
    }

    // 添加点击事件处理，确保点击不会触发长按
    const handleClick = (e: Event) => {
      if (hasTriggered) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    // 移动端
    el.addEventListener("touchstart", start, { passive: true })
    el.addEventListener("touchend", cancel)
    el.addEventListener("touchmove", cancel)
    el.addEventListener("click", handleClick, true)

    // PC 端
    el.addEventListener("mousedown", start)
    el.addEventListener("mouseup", cancel)
    el.addEventListener("mouseleave", cancel)
    el.addEventListener("click", handleClick, true)
    
    // 存储事件处理函数以便后续移除
    el._longpressHandlers = {
      start,
      cancel,
      handleClick
    }
  },
  // 🔧 新增：updated 钩子，更新 binding 引用
  updated(el: HTMLElement, binding: DirectiveBinding) {
    // 更新存储的 binding，使 start 函数能获取最新的 disabled 状态
    el._longpressBinding = binding
  },
  unmounted(el: HTMLElement) {
    // 正确移除事件监听器
    if (el._longpressHandlers) {
      const { start, cancel, handleClick } = el._longpressHandlers
      el.removeEventListener("touchstart", start)
      el.removeEventListener("touchend", cancel)
      el.removeEventListener("touchmove", cancel)
      el.removeEventListener("click", handleClick, true)
      el.removeEventListener("mousedown", start)
      el.removeEventListener("mouseup", cancel)
      el.removeEventListener("mouseleave", cancel)
      delete el._longpressHandlers
    }
    // 🔧 清理 binding 引用
    delete el._longpressBinding
  }
}

export default longpress
